/**
 * In-flight feedback for the card / results "Add to Cart" buttons.
 *
 * Horizon's ProductFormComponent only calls AddToCartComponent.disable() on the
 * max-quantity error path, so a normal add has no busy state at all — the click
 * looked inert for the ~950ms the request takes (most of it Shopify rendering
 * the cart-drawer section it asks for alongside the add).
 *
 * Two things that are easy to get wrong here, both measured:
 *  - Horizon starts the request from the button's own CLICK handler. The form's
 *    submit event fires ~1ms *after* the cart event, so hooking submit sets the
 *    flag after the clear has already run and the spinner never comes off.
 *  - `shopify:cart:lines-update` is dispatched OPTIMISTICALLY, about 56ms after
 *    the click and before the request has even been sent (response lands at
 *    ~770ms). It cannot be used to decide the request is done.
 *
 * So: mark busy on click, and clear when the /cart/add resource actually
 * finishes, observed through PerformanceObserver rather than by patching fetch.
 */
(() => {
  const BUSY = 'aria-busy';
  const MIN_VISIBLE_MS = 250; // don't let a fast add flash the spinner
  const FALLBACK_MS = 8000;
  /** @type {Map<Element, number>} */
  const pending = new Map();

  const release = (btn) => {
    const startedAt = pending.get(btn);
    if (startedAt == null) return;
    const wait = Math.max(0, MIN_VISIBLE_MS - (performance.now() - startedAt));
    pending.delete(btn);
    setTimeout(() => btn.removeAttribute(BUSY), wait);
  };

  const clearAll = () => {
    for (const btn of [...pending.keys()]) release(btn);
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = /** @type {Element | null} */ (event.target);
      const btn = target?.closest?.('button.ac-atc');
      if (!btn || !btn.closest('.ac-atc-form')) return;
      if (/** @type {HTMLButtonElement} */ (btn).disabled) return;

      btn.setAttribute(BUSY, 'true');
      pending.set(btn, performance.now());
      // Never strand the spinner if nothing else releases it.
      setTimeout(() => {
        pending.delete(btn);
        btn.removeAttribute(BUSY);
      }, FALLBACK_MS);
    },
    true
  );

  if (typeof PerformanceObserver === 'function') {
    try {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (/\/cart\/(add|change|update)/.test(entry.name)) clearAll();
        }
      }).observe({ type: 'resource', buffered: false });
    } catch {
      // Observer unsupported — the fallback timeout still releases the button.
    }
  }

  // Errors never produce a cart resource entry we can match on.
  document.addEventListener('cart:error', clearAll);
  document.addEventListener('shopify:cart:error', clearAll);
})();
